import type { Theme, ThemeOptions } from '@mui/material/styles';
import { createTheme, alpha } from '@mui/material/styles';
import type { TypographyStyleOptions } from '@mui/material/styles/createTypography';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    subtitle1Prominent: TypographyStyleOptions;
  }
  interface TypographyVariantsOptions {
    subtitle1Prominent?: TypographyStyleOptions;
  }
  interface Palette {
    elevation: { outlined: string };
  }
  interface PaletteOptions {
    elevation?: { outlined: string };
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle1Prominent: true;
  }
}
import { MuiDataGrid } from './MuiDataGrid';
import { MuiIconButton } from './MuiIconButton';
import { MuiOutlinedInput } from './MuiOutlinedInput';
import { MuiSwitch } from './MuiSwitch';
import { MuiInputAdornment } from './MuiInputAdornment';
import { MuiTooltip } from './MuiTooltip';
import { MuiDialog } from './MuiDialog';
import { MuiDialogActions } from './MuiDialogActions';
import { MuiDialogContent } from './MuiDialogContent';
import { MuiDialogTitle } from './MuiDialogTitle';
import { MuiButton } from './MuiButton';
import { MuiTextField } from './MuiTextField';
import { MuiInputLabel } from './MuiInputLabel';
import { MuiTypography } from './MuiTypography';
import { MuiAutocomplete } from './MuiAutocomplete';
import { MuiFormHelperText } from './MuiFormHelperText';

export const defaultFontFamily = 'Gellix';

const themeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: '#00283a',
      light: alpha('#00283a', 0.6),
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00b8d1',
      dark: '#009EB2',
      light: '#E6F8FA',
      contrastText: '#ffffff',
    },
    error: {
      main: '#f23d5e',
    },
    warning: {
      main: '#ffbf0a',
    },
    neutral: {
      main: '#78909C',
    },
    info: {
      main: '#303F9F',
    },
    success: {
      main: '#12a75c',
    },
    action: {
      active: alpha('#00283a', 0.56),
      hover: alpha('#00283a', 0.04),
    },
    divider: alpha('#00283a', 0.15),
    background: {
      default: '#EBF7FA',
      paper: '#FFFFFF',
    },
    elevation: {
      outlined: '#E6EAEB',
    },
    text: {
      primary: alpha('#00283a', 0.87),
      secondary: alpha('#00283a', 0.6),
      disabled: alpha('#00283a', 0.38),
    },
  },
  typography: {
    fontFamily: defaultFontFamily,
    h1: {
      fontSize: '6rem',
      fontWeight: 400,
      lineHeight: '125%',
      letterSpacing: '1.5px',
    },
    h2: {
      fontSize: '3.75rem',
      fontWeight: 400,
      lineHeight: '125%',
      letterSpacing: '0.5px',
    },
    h3: {
      fontSize: '3rem',
      fontWeight: 400,
      lineHeight: '125%',
      letterSpacing: '0.5px',
    },
    h4: {
      fontSize: '2.124rem',
      fontWeight: 400,
      lineHeight: '123.5%',
      letterSpacing: '0.25px',
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 400,
      lineHeight: '133.4%',
      letterSpacing: '0.25px',
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: '160%',
      letterSpacing: '0.25px',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '150%',
      letterSpacing: '0.15px',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: '143%',
      letterSpacing: '0.5px',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '175%',
      letterSpacing: '0.15px',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: '157%',
      letterSpacing: '0.25px',
    },
    subtitle1Prominent: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: '175%',
      letterSpacing: '0.15px',
    },
  },
  components: {
    MuiDataGrid,
    MuiIconButton,
    MuiOutlinedInput,
    MuiSwitch,
    MuiInputAdornment,
    MuiTextField,
    MuiInputLabel,
    MuiTypography,
    MuiTooltip,
    MuiDialog,
    MuiButton,
    MuiAutocomplete,
    MuiFormHelperText,
    MuiDialogActions,
    MuiDialogContent,
    MuiDialogTitle,
  },
};

const lightTheme: Theme = createTheme(themeOptions);

export default lightTheme;
